import { useId, useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import CloseIcon from '@mui/icons-material/Close'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import {
    Alert,
    AlertTitle,
    Button,
    ButtonBase,
    Container,
    IconButton,
    Paper,
    Popover,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { useFormik } from 'formik'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import * as Yup from 'yup'

import {
    CONTACT_VERIFICATION_IDENTITY_DATA_KEY,
    setItemToLS,
    useSaveContactVerificationData,
} from '../../core/api'
import { countries } from './countries'
import {
    requiresProofOfAddress,
    shouldRequireOrganizationField,
    validateCommercialRegistrationQueryCode,
    validateNIF,
    validateZipcode,
} from './identity-helpers'

function formatFileSize(size) {
    if (!Number.isFinite(size) || size <= 0) {
        return '0 KB'
    }

    return `${(size / 1024).toFixed(1)} KB`
}

function getFieldProps(formik, field) {
    return {
        name: field,
        value: formik.values[field],
        onChange: formik.handleChange,
        onBlur: formik.handleBlur,
        error: Boolean(formik.touched[field] && formik.errors[field]),
        helperText: formik.touched[field] ? formik.errors[field] : '',
    }
}

export default function VerificationIdentityFormCard({
    title,
    description = null,
    initialValues,
    email,
    phone,
    noPreviousData = false,
}) {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const { verificationToken = '' } = useParams()
    const uploadInputId = useId()
    const saveContactData = useSaveContactVerificationData()
    const [submitError, setSubmitError] = useState('')
    const [exampleAnchor, setExampleAnchor] = useState(null)

    function handleExampleOpen(event) {
        setExampleAnchor(event.currentTarget)
    }

    function handleExampleClose() {
        setExampleAnchor(null)
    }

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            vat: initialValues.vat || '',
            name: initialValues.name || '',
            org: initialValues.org || '',
            commercialRegistrationQueryCode: initialValues.commercialRegistrationQueryCode || '',
            street: initialValues.street || '',
            city: initialValues.city || '',
            zipcode: initialValues.zipcode || '',
            country: initialValues.country || '',
            documentFile: null,
        },
        validationSchema: Yup.object({
            vat: Yup.string()
                .trim()
                .required(t('verification.validation.required'))
                .test('pt-nif', t('verification.validation.invalidNif'), (value, context) => {
                    const normalizedValue = `${value || ''}`.trim()
                    const country = `${context.parent.country || ''}`.trim().toUpperCase()

                    if (country !== 'PT') {
                        return true
                    }

                    return validateNIF(normalizedValue)
                }),
            name: Yup.string().trim().required(t('verification.validation.required')),
            org: Yup.string().when(['country', 'vat'], {
                is: (country, vat) => shouldRequireOrganizationField(country, vat),
                then: (schema) => schema.trim().required(t('verification.validation.required')),
                otherwise: (schema) => schema.notRequired(),
            }),
            commercialRegistrationQueryCode: Yup.string()
                .trim()
                .notRequired()
                .test('commercial-registration-query-code', t('verification.validation.invalidCommercialRegistrationQueryCode'), (value) =>
                    !value || validateCommercialRegistrationQueryCode(value),
                ),
            street: Yup.string().trim().required(t('verification.validation.required')),
            city: Yup.string().trim().required(t('verification.validation.required')),
            zipcode: Yup.string()
                .trim()
                .required(t('verification.validation.required'))
                .test('pt-zipcode', t('verification.validation.invalidZipcode'), (value, context) =>
                    validateZipcode(context.parent.country, value),
                ),
            country: Yup.string().trim().required(t('verification.validation.required')),
            documentFile: Yup.mixed()
                .test('pdf', t('verification.documentPdfOnly'), (value, context) => {
                    if (
                        !value ||
                        !requiresProofOfAddress(
                            context.parent.country,
                            context.parent.vat,
                            context.parent.commercialRegistrationQueryCode,
                        )
                    ) {
                        return true
                    }

                    return value.type === 'application/pdf' || value.name?.toLowerCase().endsWith('.pdf')
                })
                .when(['country', 'vat', 'commercialRegistrationQueryCode'], {
                    is: (country, vat, commercialRegistrationQueryCode) =>
                        requiresProofOfAddress(country, vat, commercialRegistrationQueryCode),
                    then: (schema) => schema.required(t('verification.validation.required')),
                    otherwise: (schema) => schema.notRequired(),
                }),
        }),
        onSubmit: async (values) => {
            setSubmitError('')

            const payload = {
                name: values.name.trim(),
                org: shouldRequireOrganizationField(values.country, values.vat) ? values.org.trim() : '',
                commercialRegistrationQueryCode: shouldRequireOrganizationField(values.country, values.vat)
                    ? values.commercialRegistrationQueryCode.trim()
                    : '',
                street: values.street.trim(),
                city: values.city.trim(),
                zipcode: values.zipcode.trim(),
                country: values.country,
                email,
                phone,
                mobile: '',
            }

            try {
                await saveContactData.mutateAsync({
                    contactData: payload,
                    proofOfAddress: requiresProofOfAddress(values.country, values.vat, values.commercialRegistrationQueryCode)
                        ? values.documentFile
                        : null,
                })
                setItemToLS(CONTACT_VERIFICATION_IDENTITY_DATA_KEY, {
                    source: 'manual',
                    ...payload,
                })
                navigate(`/verify/${verificationToken}/review`)
            } catch (error) {
                setSubmitError(error?.response?.data?.message || error?.message || t('verification.saveContactDataError'))
            }
        },
        validateOnMount: true,
    })

    const selectedCountry = countries.find(
        (country) => country.code === formik.values.country || country.label === formik.values.country,
    )
    const shouldShowOrgField = shouldRequireOrganizationField(formik.values.country, formik.values.vat)
    const documentRequired = requiresProofOfAddress(
        formik.values.country,
        formik.values.vat,
        formik.values.commercialRegistrationQueryCode,
    )

    function handleFileChange(event) {
        const file = event.target.files?.[0] || null
        formik.setFieldTouched('documentFile', true, false)
        formik.setFieldValue('documentFile', file)
    }

    function removeFile() {
        formik.setFieldTouched('documentFile', true, false)
        formik.setFieldValue('documentFile', null)
    }

    const documentError = formik.touched.documentFile ? formik.errors.documentFile : ''

    return (
        <Container maxWidth="sm" sx={{ py: { xs: 3, md: 5 }, flexGrow: 1 }}>
            <Stack spacing={3} component="form" onSubmit={formik.handleSubmit}>
                <Stack>
                    <Typography variant="h3" gutterBottom>
                        {title}
                    </Typography>
                    {noPreviousData && (
                        <Alert severity="warning">
                            <AlertTitle>{t('verification.noPreviousDataTitle')}</AlertTitle>
                            <Stack spacing={1} mt={2}>
                                <Typography variant="inherit">
                                    {t('verification.noPreviousDataDescription')}
                                </Typography>

                                <Typography variant="inherit" fontWeight={500}>
                                    {t('verification.manualValidationDelayWarning')}
                                </Typography>
                            </Stack>
                        </Alert>
                    )}
                    <Alert severity="warning">
                        {t('verification.fullNameWarning')}
                    </Alert>
                    {description && (
                        <Typography color="text.secondary">{description}</Typography>
                    )}
                </Stack>
                {submitError ? <Alert severity="error">{submitError}</Alert> : null}

                <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 } }}>
                    <Stack spacing={2}>
                        <TextField required label={t('verification.fields.vat')} {...getFieldProps(formik, 'vat')} disabled={true} />
                        <TextField required label={t('verification.fields.name')} {...getFieldProps(formik, 'name')} />
                        {shouldShowOrgField ? <TextField required label={t('verification.fields.org')} {...getFieldProps(formik, 'org')} /> : null}
                        <TextField required label={t('verification.fields.street')} {...getFieldProps(formik, 'street')} />
                        <TextField required label={t('verification.fields.city')} {...getFieldProps(formik, 'city')} />
                        <TextField required label={t('verification.fields.zipcode')} {...getFieldProps(formik, 'zipcode')} />
                        <Autocomplete
                            fullWidth
                            autoHighlight
                            options={countries}
                            value={selectedCountry || null}
                            getOptionLabel={(option) => option.label}
                            onChange={(_event, option) => {
                                formik.setFieldTouched('country', true, false)
                                formik.setFieldValue('country', option?.code || '')
                            }}
                            onBlur={() => formik.setFieldTouched('country', true, true)}
                            renderOption={(props, option) => {
                                const { key, ...optionProps } = props

                                return (
                                    <Box
                                        key={key}
                                        component="li"
                                        sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                                        {...optionProps}
                                    >
                                        <img
                                            loading="lazy"
                                            width="20"
                                            srcSet={`https://flagcdn.com/w40/${option?.code?.toLowerCase()}.png 2x`}
                                            src={`https://flagcdn.com/w20/${option?.code?.toLowerCase()}.png`}
                                            alt=""
                                        />
                                        {option.label} ({option.code})
                                    </Box>
                                )
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    label={t('verification.fields.country')}
                                    error={Boolean(formik.touched.country && formik.errors.country)}
                                    helperText={formik.touched.country ? formik.errors.country : ''}
                                />
                            )}
                        />
                    </Stack>
                </Paper>

                <Stack spacing={1.5}>
                    <Typography variant="h4">{t('verification.contactsTitle')}</Typography>
                    <Paper variant="outlined" sx={{ p: { xs: 3, md: 4 } }}>
                        <Stack spacing={1}>
                            <Stack>
                                <Typography variant="caption" color="text.secondary">{t('verification.emailLabel')}</Typography>
                                <Typography>{email || t('verification.notAvailable')}</Typography>
                            </Stack>
                            <Stack>
                                <Typography variant="caption" color="text.secondary">{t('verification.phoneLabel')}</Typography>
                                <Typography>{phone || t('verification.notAvailable')}</Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                </Stack>

                {shouldShowOrgField ? (
                    <Stack spacing={1.5}>
                        <Typography variant="h4">{t('verification.fields.commercialRegistrationQueryCode')}</Typography>
                        <TextField
                            {...getFieldProps(formik, 'commercialRegistrationQueryCode')}
                        />
                        <Typography variant="caption">{t('verification.optional')}</Typography>
                    </Stack>
                ) : null}

                <Stack spacing={1.5} sx={{ display: documentRequired ? undefined : 'none' }}>
                    <Stack>
                        <Typography variant="h4">{t('verification.fiscalDocumentTitle')}</Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('verification.fiscalDocumentInstructions')}{' '}
                            <Box
                                component="span"
                                onClick={handleExampleOpen}
                                sx={{
                                    textDecoration: 'underline',
                                    cursor: 'pointer',
                                }}
                            >
                                {t('verification.fiscalDocumentExamplesLink')}
                            </Box>
                        </Typography>
                        <Alert severity="error" sx={{
                            fontWeight: 600,
                            whiteSpace: 'pre-line'
                        }}>
                            <Trans
                                i18nKey="verification.fiscalDocumentIdentityWarning"
                            />
                        </Alert>
                    </Stack>
                    <Popover
                        id={exampleAnchor ? 'simple-popover' : undefined}
                        open={Boolean(exampleAnchor)}
                        anchorEl={exampleAnchor}
                        onClose={handleExampleClose}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                    >
                        <Stack
                            p={2}
                            sx={{
                                maxWidth: '62ch',
                            }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                {t('verification.fiscalDocumentExampleIndividuals')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('verification.fiscalDocumentExampleOrganizations')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('verification.fiscalDocumentExampleAlternative')}
                            </Typography>
                        </Stack>
                    </Popover>
                    {documentError ? <Alert severity="error">{documentError}</Alert> : null}
                    {formik.values.documentFile ? (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                                <Stack direction="row" spacing={1.5} alignItems="center">
                                    <DescriptionOutlinedIcon fontSize="small" />
                                    <div>
                                        <Typography>{formik.values.documentFile.name}</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatFileSize(formik.values.documentFile.size)}
                                        </Typography>
                                    </div>
                                </Stack>
                                <IconButton aria-label={t('verification.removeFile')} onClick={removeFile}>
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                        </Paper>
                    ) : (
                        <ButtonBase component="label" htmlFor={uploadInputId}>
                            <Paper variant="outlined" sx={{ p: 2, borderStyle: 'dashed', width: '100%' }}>
                                <Stack spacing={1.5} flexGrow={1}>
                                    <Stack direction="row" spacing={1.5} alignItems="center">
                                        <UploadFileOutlinedIcon fontSize="small" color="action" />
                                        <Typography color="text.secondary">{t('verification.fiscalDocumentDescription')}</Typography>
                                    </Stack>
                                    <input id={uploadInputId} hidden type="file" accept="application/pdf" onChange={handleFileChange} />
                                </Stack>
                            </Paper>
                        </ButtonBase>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        {t('verification.documentPdfOnly')}
                    </Typography>
                </Stack>
                <Button
                    type="submit" variant="contained" color="primary" disabled={!formik.isValid || saveContactData.isPending}
                    loading={saveContactData.isPending}
                >
                    {t('verification.continue')}
                </Button>
            </Stack>
        </Container>
    )
}
